import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";
import PortugalSpainQuickFitCheck from "@/components/PortugalSpainQuickFitCheck";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const quickFitBullets = [
  "retirement-minded and comparing Southern Europe",
  "balancing healthcare confidence with affordability",
  "trying to understand which country is more practical to pursue",
  "looking for a calmer lifestyle with strong day-to-day quality of life",
  "deciding where to focus deeper research first"
];

const sideBySideItems = [
  {
    title: "Long-term affordability",
    portugal: "Often feels easier to sustain on a moderate retirement budget, especially outside the most expensive hotspots.",
    spain: "Can still work well, but affordability may tighten faster depending on region and lifestyle expectations."
  },
  {
    title: "Healthcare confidence",
    portugal: "Strong enough for many retirees, especially those comfortable with a balanced public-private approach.",
    spain: "Often feels like the stronger option if healthcare confidence is one of the biggest deciding factors."
  },
  {
    title: "Residency practicality",
    portugal: "Can look more approachable for retirees who want a realistic path without feeling overly complex.",
    spain: "Still viable, but the practical experience may feel more demanding depending on the route and documentation burden."
  },
  {
    title: "Lifestyle and day-to-day feel",
    portugal: "Often appeals to retirees looking for a calmer pace, coastal access, and easier daily rhythm.",
    spain: "Can appeal more if you want stronger urban variety, richer city energy, or a broader mix of lifestyles."
  },
  {
    title: "Ease of transition",
    portugal: "Often feels slightly easier for Americans looking for a softer landing and a more relaxed adjustment curve.",
    spain: "Still very appealing, but the right fit can depend more heavily on region, expectations, and daily logistics."
  },
  {
    title: "Infrastructure and convenience",
    portugal: "Works well for many retirees, but can feel lighter depending on where you settle.",
    spain: "Often feels stronger if dependable infrastructure and broader service access matter a lot to you."
  }
];

const decisionQuestions = [
  "Do you want lower ongoing cost pressure, or are you willing to spend more for stronger healthcare and infrastructure?",
  "Do you want a calmer, simpler day-to-day lifestyle, or a broader set of city and regional options?",
  "How much does the practical feel of the residency path matter in your overall comfort level?",
  "Are you choosing based on a full retirement lifestyle, or on one or two standout features that may matter less once daily life begins?"
];

const answerShifters = [
  "If healthcare rises to the top of your list, Spain may become more attractive.",
  "If affordability becomes tighter in real life, Portugal may start to look stronger.",
  "If you care more about city energy, transport, and larger urban options, Spain may gain ground.",
  "If you want a calmer pace and a more manageable day-to-day cost structure, Portugal may hold its lead.",
  "If the residency path feels less workable in one country than expected, the comparison can change fast."
];

const RETIREMENT_QUICK_FIT_QUESTIONS = [
  {
    id: "retirement_priority",
    question: "What matters most in your retirement?",
    options: [
      { label: "Healthcare confidence", country: "ES" },
      { label: "Making retirement income go further", country: "PT" },
    ],
  },
  {
    id: "healthcare_certainty",
    question: "How much healthcare certainty do you want?",
    options: [
      { label: "I want the strongest healthcare system possible.", country: "ES" },
      { label: "I'm comfortable with excellent care if my costs stay lower.", country: "PT" },
    ],
  },
  {
    id: "retirement_lifestyle",
    question: "What retirement lifestyle sounds most appealing?",
    options: [
      { label: "Quiet towns and a relaxed pace", country: "PT" },
      { label: "A balance of relaxation and activity", country: "ES" },
    ],
  },
  {
    id: "expense_predictability",
    question: "How important is keeping your monthly expenses predictable?",
    options: [
      { label: "Very important", country: "PT" },
      { label: "Some flexibility is okay", country: "ES" },
    ],
  },
  {
    id: "retirement_imagined",
    question: "Which feels more like the retirement you imagine?",
    options: [
      { label: "Familiar surroundings and routines", country: "PT" },
      { label: "More adventure and exploration", country: "ES" },
    ],
  },
];

const RETIREMENT_QUICK_FIT_RESULTS = {
  PT: {
    headline: "Portugal may deserve the first closer look for retirement",
    body: "Your answers lean toward Portugal because lower ongoing cost pressure, a calmer day-to-day rhythm, and a softer retirement transition appear to matter more right now. Spain may still deserve a closer look if stronger healthcare confidence and broader regional variety begin to carry more weight.",
  },
  ES: {
    headline: "Spain may deserve the first closer look for retirement",
    body: "Your answers lean toward Spain because healthcare confidence, a more active day-to-day mix, and broader regional variety appear to matter more in your retirement picture. Portugal may still deserve a closer look if predictability, affordability, and a calmer pace rise higher in importance.",
  },
  balanced: {
    headline: "Your retirement priorities are more balanced — both countries likely deserve a deeper comparison",
    body: "Neither country clearly pulls ahead from these answers alone. The next useful step is to weigh healthcare, monthly cost predictability, retirement lifestyle, and residency practicality together before narrowing the lead.",
  },
};

const RETIREMENT_FAQ_ITEMS = [
  {
    question: "Does Spain usually come out ahead for healthcare in retirement?",
    answer:
      "Spain often feels stronger when healthcare reassurance is one of the biggest deciding factors. Portugal can still work very well for retirees, especially if a balanced public-private approach feels acceptable and lower ongoing costs matter more.",
  },
  {
    question: "Is Portugal usually easier on a retirement budget?",
    answer:
      "Portugal often looks easier to sustain over time, especially outside the highest-cost areas. Spain can still be workable, but the budget picture usually depends more heavily on region, lifestyle expectations, and how much city access matters to you.",
  },
  {
    question: "Should retirees choose between Portugal and Spain based on cost alone?",
    answer:
      "Usually no. Cost matters, but the stronger retirement fit also depends on healthcare confidence, day-to-day rhythm, residency practicality, and whether the country still feels workable once the novelty wears off.",
  },
  {
    question: "Can the better retirement answer change as priorities change?",
    answer:
      "Yes. A retiree who initially prioritizes affordability may lean toward Portugal, while someone who places more weight on healthcare confidence or broader urban variety may lean toward Spain. Different priorities naturally produce different answers.",
  },
];

export default function PortugalVsSpainForAmericanRetirees() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="portugal-spain-comparison-page">
      <SeoArticleSchema
        headline="Portugal vs Spain for American Retirees"
        description="Compare Portugal and Spain for retirement abroad across cost, healthcare, residency practicality, and day-to-day fit so you can see which option suits your priorities more realistically."
        pathname="/insights/portugal-vs-spain-for-american-retirees"
        keywords={[
          "Portugal vs Spain for American retirees",
          "retire in Portugal or Spain",
          "Portugal Spain retirement comparison",
        ]}
      />
      <Header />

      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-[2rem] border border-stone-200/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="comparison-hero-section">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="comparison-hero-accent" />
            <p className="rc-section-kicker mb-4">Retirement Comparison</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="comparison-page-title">
              Portugal vs Spain for American Retirees
            </h1>
            <div className="mt-6 max-w-4xl space-y-5 text-base leading-8 text-stone-600" data-testid="comparison-page-intro">
              <p>
                If you’re deciding between Portugal and Spain for retirement, the real question usually is not which country sounds better on paper. It is which one fits your priorities more realistically once healthcare, affordability, residency practicality, and day-to-day lifestyle are weighed together.
              </p>
              <p>
                This page is designed to help you compare that tradeoff more clearly before you commit.
              </p>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="comparison-key-takeaway"
            paragraphs={[
              "Portugal often looks stronger for American retirees who want lower ongoing cost pressure, a calmer day-to-day rhythm, and a residency path that feels more approachable.",
              "Spain often looks stronger for retirees who are willing to accept more cost or complexity in exchange for stronger healthcare confidence, broader city choice, and heavier infrastructure. The better answer depends on which of those tradeoffs matters more to you.",
            ]}
          />

          <PortugalSpainQuickFitCheck
            quickFitType="portugal_spain_retirees"
            kicker="Embedded retirement tool"
            questions={RETIREMENT_QUICK_FIT_QUESTIONS}
            resultContent={RETIREMENT_QUICK_FIT_RESULTS}
          />

          <section className="rounded-[2rem] border border-stone-200/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="comparison-faq-section">
            <div className="max-w-4xl">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="comparison-faq-kicker">Questions retirees usually still have</p>
              <h2 className="mt-3 font-manrope text-3xl text-stone-900" data-testid="comparison-faq-title">Still Wondering About Portugal vs. Spain?</h2>
              <p className="mt-4 text-base leading-8 text-stone-600" data-testid="comparison-faq-description">
                These are the retirement-specific questions that usually come up once one country begins to pull ahead but you still want more confidence before making the bigger decision.
              </p>
            </div>

            <Accordion type="single" collapsible className="mt-7 space-y-3" data-testid="comparison-faq-accordion">
              {RETIREMENT_FAQ_ITEMS.map((item, index) => (
                <AccordionItem
                  key={item.question}
                  value={`retirement-faq-${index + 1}`}
                  className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5"
                  data-testid={`comparison-faq-item-${index + 1}`}
                >
                  <AccordionTrigger
                    className="py-5 text-left font-manrope text-lg text-stone-900 no-underline hover:no-underline"
                    data-testid={`comparison-faq-trigger-${index + 1}`}
                  >
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="pb-5 text-sm leading-7 text-stone-600" data-testid={`comparison-faq-answer-${index + 1}`}>
                      {item.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="grid gap-6 lg:grid-cols-[1.05fr_1.2fr]" data-testid="comparison-context-grid">
            <article className="rounded-[2rem] border border-stone-200/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="comparison-audience-section">
              <h2 className="font-manrope text-2xl text-stone-900">Who is this comparison actually for?</h2>
              <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600" data-testid="comparison-audience-list">
                {quickFitBullets.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="rounded-[2rem] border border-stone-200/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="comparison-quick-read-section">
              <h2 className="font-manrope text-2xl text-stone-900">What is the quick read on Portugal vs Spain?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600">
                <p>
                  For many American retirees, Portugal may feel stronger when affordability, lifestyle ease, and a more manageable day-to-day cost matter most.
                </p>
                <p>
                  Spain may feel stronger when healthcare confidence, infrastructure, and broader city options matter enough to justify a potentially heavier cost picture.
                </p>
                <p className="text-stone-800">
                  The better fit often comes down to which tradeoff matters more in real life: <span className="font-semibold">cost and ease</span> or <span className="font-semibold">healthcare and infrastructure strength</span>.
                </p>
              </div>
            </article>
          </section>

          <section className="rounded-[2rem] border border-stone-200/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="comparison-side-by-side-section">
            <h2 className="font-manrope text-2xl text-stone-900">How do the two countries compare side by side?</h2>
            <div className="mt-8 space-y-4">
              {sideBySideItems.map((item, index) => (
                <article key={item.title} className="rounded-[1.6rem] border border-stone-200 bg-stone-50/80 p-5" data-testid={`comparison-side-by-side-item-${index + 1}`}>
                  <h3 className="font-manrope text-xl text-stone-900">{item.title}</h3>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div className="rounded-[1.25rem] border border-stone-200/80 bg-white/88 p-4" data-testid={`comparison-portugal-block-${index + 1}`}>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Portugal</p>
                      <p className="mt-2 text-sm leading-7 text-stone-700">{item.portugal}</p>
                    </div>
                    <div className="rounded-[1.25rem] border border-stone-200/80 bg-white/88 p-4" data-testid={`comparison-spain-block-${index + 1}`}>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Spain</p>
                      <p className="mt-2 text-sm leading-7 text-stone-700">{item.spain}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="comparison-decision-grid">
            <article className="rounded-[2rem] border border-stone-200/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="comparison-what-decides-section">
              <h2 className="font-manrope text-2xl text-stone-900">What usually decides the answer?</h2>
              <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600">
                {decisionQuestions.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-stone-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="rounded-[2rem] border border-stone-200/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="comparison-what-could-change-section">
              <h2 className="font-manrope text-2xl text-stone-900">What could change the answer once you dig deeper?</h2>
              <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600">
                {answerShifters.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          </section>

          <section className="rounded-[2rem] border border-stone-200/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="comparison-how-to-use-section">
            <h2 className="font-manrope text-2xl text-stone-900">How should you use this comparison?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600">
              <p>
                Use this page to narrow the field, not to make the whole decision by itself.
              </p>
              <p>
                If Portugal and Spain are both still in play, the next step is usually to compare them based on your actual retirement budget, how much healthcare confidence matters to you, the kind of day-to-day lifestyle you want, and which practical path feels more realistic to pursue.
              </p>
              <p>
                If you want to step back before committing to this pair, it can help to revisit{" "}
                <Link to="/insights/best-countries-for-americans-to-move-abroad" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="comparison-broader-americans-link">
                  the broader Americans-moving-abroad guide
                </Link>
                {" "}or sharpen the shortlist with{" "}
                <Link to="/insights/create-country-shortlist-for-moving-abroad" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="comparison-shortlist-link">
                  a more disciplined shortlist process
                </Link>
                .
              </p>
            </div>
          </section>

          <ContextualLinkPanel
            kicker="Continue exploring"
            title="If you are not ready for the full assessment yet, these are the most useful next pages from here."
            description="You can keep learning without losing the retirement context: reopen the wider field for Americans, preview how a fuller Blueprint handles the shortlist, or compare the broader Portugal–Spain framing before you personalize the answer."
            testIdPrefix="comparison-next-steps"
            items={[
              {
                slug: "americans-guide",
                eyebrow: "Reopen the wider field",
                title: "Review the broader guide for Americans",
                description: "Step back into the wider shortlist if you want to confirm Portugal and Spain still deserve to lead.",
                ctaLabel: "View the broader guide",
                to: "/insights/best-countries-for-americans-to-move-abroad",
              },
              {
                slug: "sample-blueprint",
                eyebrow: "See the paid experience",
                title: "Preview a Sample Blueprint",
                description: "See how a head-to-head comparison becomes a fuller recommendation with tradeoffs and next steps.",
                ctaLabel: "Preview the sample",
                to: "/sample-plan",
              },
              {
                slug: "broader-comparison",
                eyebrow: "Use the broader framing",
                title: "See the broader Portugal vs Spain comparison",
                description: "Compare the same pair through a more general living-abroad lens if you want another angle before deciding.",
                ctaLabel: "Open the comparison",
                to: "/insights/portugal-vs-spain-living-abroad",
              },
            ]}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
}